import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useMoneyDisplay } from '@eventespresso/services';
import { MoneyInputWrapper } from '..';
import { NumberInput } from '..';

import type { LocalizeInputProp } from '@eventespresso/adapters';
import type { NumberProps } from '../NumberInput';

export interface MoneyInputProps extends NumberProps {
	amount: number;
	// why onUpdate and not onChange? because NumberProps.onChange is already defined
	onUpdate: (amount: number) => void;
}

export const MoneyInput: React.FC<MoneyInputProps> = ({ amount, isDisabled, onUpdate, ...props }) => {
	const { currency, localizeMoney, parseLocalizedMoney } = useMoneyDisplay();
	const [moneyAmount, setAmount] = useState(amount ? localizeMoney(amount) : '');
	const [displayValue, setDisplayValue] = useState(moneyAmount);
	const [focus, setFocus] = useState<boolean>(false);
	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const localizeInput = useMemo<Required<LocalizeInputProp>>(
		() => ({ decimalMark: currency.decimalMark, thousandsSeparator: currency.thousandsSeparator }),
		[currency]
	);

	const className = useMemo(
		() => classNames('ee-input__money-field', moneyAmount === '' && 'ee-input__money-field--has-error'),
		[moneyAmount]
	);

	const onChange = useCallback((eventOrValue: React.ChangeEvent<HTMLInputElement> | string) => {
		let newAmount: string;
		if (typeof eventOrValue === 'string') {
			// Adapter-style: (valueAsString, valueAsNumber)
			newAmount = eventOrValue;
		} else if (eventOrValue && 'currentTarget' in eventOrValue) {
			// React event-style: (event)
			newAmount = eventOrValue.currentTarget.value;
		} else {
			newAmount = '';
		}
		setAmount(newAmount);
		setDisplayValue(newAmount);
	}, []);

	const onBlur = useCallback<On.Blur>(
		(event) => {
			const newAmount = event.currentTarget.value;
			const parsedAmount = parseLocalizedMoney(newAmount);
			setAmount(localizeMoney(newAmount));
			onUpdate(parsedAmount);
			setFocus(false);
		},
		[localizeMoney, parseLocalizedMoney, onUpdate]
	);

	useEffect(() => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}
		if (focus) {
			// debounce formatting
			debounceTimeout.current = setTimeout(() => {
				// Only format if the value is a valid number
				const parsed = parseLocalizedMoney(moneyAmount);
				const newValue = !isNaN(parsed) && moneyAmount !== '' ? localizeMoney(parsed) : moneyAmount;
				if (newValue !== displayValue) {
					setDisplayValue(newValue);
				}
			}, 2000);
		} else {
			// update when amount prop changes, but only when not focusing on the input field
			const localizedAmount = amount ? localizeMoney(amount) : '';
			if (localizedAmount !== displayValue) {
				setAmount(localizedAmount);
				setDisplayValue(localizedAmount);
			}
		}
		return () => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}
		};
	}, [focus, moneyAmount, localizeMoney, parseLocalizedMoney, displayValue, amount]);

	const onFocus = useCallback<On.Focus>(() => {
		setFocus(true);
		setDisplayValue(moneyAmount);
	}, [moneyAmount]);

	return (
		<MoneyInputWrapper sign={currency?.sign} signB4={currency?.signB4} disabled={isDisabled}>
			<NumberInput
				{...props}
				disabled={isDisabled}
				inputClass='ee-input'
				min={0}
				onBlur={onBlur}
				onChange={onChange}
				onFocus={onFocus}
				showStepper={false}
				value={displayValue}
				wrapperClass={className}
				localizeInput={localizeInput}
			/>
		</MoneyInputWrapper>
	);
};

namespace On {
	export type Change = NoUndefined<NumberProps['onChange']>;
	export type Blur = NoUndefined<NumberProps['onBlur']>;
	export type Focus = React.FocusEventHandler<HTMLInputElement>;

	type NoUndefined<T extends any> = Exclude<T, undefined>;
}
